import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Crown, Ticket, MapPin, CalendarClock, ArrowRight, RefreshCcw, RotateCcw, Loader2, AlertTriangle, CreditCard } from "lucide-react";
import { useLang } from "../../context/LanguageContext";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../hooks/use-toast";
import api from "../../api";

const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }); }
  catch { return ""; }
};

const daysUntil = (iso) => {
  try {
    const ms = new Date(iso).getTime() - Date.now();
    return Math.max(0, Math.ceil(ms / 86400000));
  } catch { return 0; }
};

// The one place a shop can see what it is paying for and what is left of it this month.
export const PlanCard = ({ credits }) => {
  const { t } = useLang();
  const { user, refresh } = useAuth();
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);
  const [portalBusy, setPortalBusy] = useState(false);
  if (!credits) return null;

  const paid = credits.plan !== "free" && !credits.planLifetime && credits.planExpiresAt;
  const autoRenew = user?.autoRenew !== false;
  const graceLeft = credits.inGrace && credits.graceUntil ? daysUntil(credits.graceUntil) : 0;
  const toggleRenew = async () => {
    setBusy(true);
    try {
      await api.post(autoRenew ? "/my/subscription/cancel" : "/my/subscription/resume");
      await refresh();
      toast({ title: autoRenew ? t("pc.renewOffToast") : t("pc.renewOnToast") });
    } catch { toast({ title: t("pc.renewFail") }); }
    finally { setBusy(false); }
  };

  const openBilling = async () => {
    setPortalBusy(true);
    try {
      const { data } = await api.post("/my/subscription/portal");
      if (data?.url) window.location.href = data.url;
    } catch (e) {
      toast({ title: t("pc.billingFail"), description: e.response?.data?.detail || "" });
    } finally { setPortalBusy(false); }
  };

  const cap = credits.monthlyAllowance;
  const total = credits.allowanceTotal != null ? credits.allowanceTotal : cap;
  const used = credits.dealsThisMonth || 0;
  const unlimited = cap == null;
  const pct = unlimited ? 0 : Math.min(100, Math.round((used / Math.max(1, total)) * 100));
  const left = unlimited ? null : Math.max(0, total - used);
  const tight = !unlimited && left <= 1;

  return (
    <div className="bg-card border border-border rounded-2xl p-5" data-testid="plan-card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-extrabold text-foreground flex items-center gap-2" data-testid="plan-card-tier">
            <Crown size={18} className="text-[#16a34a]" /> {t("pc.yourPlan", { plan: credits.planLabel })}
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5" data-testid="plan-card-renews">
            {credits.plan === "free" ? t("pc.freeForever")
              : credits.planLifetime ? t("pc.lifetime")
                : credits.planExpiresAt ? t("pc.renews", { date: fmtDate(credits.planExpiresAt) }) : ""}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link to="/pricing" data-testid="plan-card-topup"
            className="inline-flex items-center gap-1.5 text-xs font-bold border border-border hover:border-[#16a34a] px-3 py-2 rounded-xl transition-colors">
            <Ticket size={13} className="text-[#16a34a]" /> {t("pc.buyDeals")}
          </Link>
          {credits.plan !== "business_plus" && (
            <Link to="/pricing" data-testid="plan-card-upgrade"
              className="inline-flex items-center gap-1.5 text-xs font-bold bg-[#16a34a] hover:bg-[#15803d] text-white px-3 py-2 rounded-xl transition-colors">
              {t("pc.upgrade")} <ArrowRight size={13} />
            </Link>
          )}
        </div>
      </div>

      {credits.inGrace && (
        <div className="mt-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-xl border border-[#e11d48]/40 bg-[#e11d48]/10 px-4 py-3" data-testid="plan-card-grace">
          <p className="text-sm font-bold text-[#b91c1c] dark:text-[#fca5a5] flex items-center gap-2" data-testid="plan-card-grace-msg">
            <AlertTriangle size={16} className="shrink-0" />
            {t("pc.graceMsg", { n: graceLeft })}
          </p>
          <div className="flex items-center gap-2 shrink-0">
            {credits.canManageBilling && (
              <button onClick={openBilling} disabled={portalBusy} data-testid="plan-card-grace-billing"
                className="inline-flex items-center gap-1.5 text-xs font-bold rounded-xl px-3 py-2 border border-border text-muted-foreground hover:bg-secondary transition-colors disabled:opacity-60">
                {portalBusy ? <Loader2 size={13} className="animate-spin" /> : <CreditCard size={13} />}
                {t("pc.updateCard")}
              </button>
            )}
            <Link to="/pricing" data-testid="plan-card-grace-renew"
              className="inline-flex items-center gap-1.5 text-xs font-bold rounded-xl px-3 py-2 bg-[#e11d48] hover:bg-[#be123c] text-white transition-colors">
              <RotateCcw size={13} /> {t("pc.renewNow")}
            </Link>
          </div>
        </div>
      )}

      {paid && (
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border bg-secondary/40 px-4 py-3" data-testid="plan-card-autorenew">
          <p className="text-sm font-semibold text-foreground flex items-center gap-2" data-testid="plan-card-autorenew-status">
            <RefreshCcw size={15} className={autoRenew ? "text-[#16a34a]" : "text-muted-foreground"} />
            {autoRenew ? t("pc.autoRenewsOn", { date: fmtDate(credits.planExpiresAt) })
                       : t("pc.autoRenewOff", { date: fmtDate(credits.planExpiresAt) })}
          </p>
          <div className="flex items-center gap-2">
          {credits.canManageBilling && (
            <button onClick={openBilling} disabled={portalBusy} data-testid="plan-card-billing"
              className="inline-flex items-center gap-1.5 text-xs font-bold rounded-xl px-3 py-2 border border-border text-muted-foreground hover:bg-secondary transition-colors disabled:opacity-60">
              {portalBusy ? <Loader2 size={13} className="animate-spin" /> : <CreditCard size={13} />}
              {t("pc.updateCard")}
            </button>
          )}
          <button onClick={toggleRenew} disabled={busy} data-testid="plan-card-autorenew-toggle"
            className={`inline-flex items-center gap-1.5 text-xs font-bold rounded-xl px-3 py-2 transition-colors disabled:opacity-60 ${autoRenew ? "border border-border text-muted-foreground hover:bg-secondary" : "bg-[#16a34a] hover:bg-[#15803d] text-white"}`}>
            {busy ? <Loader2 size={13} className="animate-spin" /> : <RotateCcw size={13} />}
            {autoRenew ? t("pc.cancelAutoRenew") : t("pc.resumeAutoRenew")}
          </button>
          </div>
        </div>
      )}

      <div className="mt-4">
        <div className="flex items-end justify-between gap-3">
          <p className="text-sm font-bold text-foreground" data-testid="plan-card-quota">
            {unlimited ? t("pc.unlimitedThisMonth") : t("pc.nOfMThisMonth", { n: used, m: total })}
          </p>
          <p className={`text-xs font-semibold ${tight ? "text-[#b8860b]" : "text-muted-foreground"}`} data-testid="plan-card-left">
            {unlimited ? "" : left === 0 ? t("pc.noneLeft") : t("pc.nLeft", { n: left })}
          </p>
        </div>
        {!unlimited && (
          <div className="mt-2 h-2 rounded-full bg-secondary overflow-hidden">
            <div className={`h-full rounded-full transition-all duration-500 ${pct >= 100 ? "bg-[#e11d48]" : pct >= 80 ? "bg-[#b8860b]" : "bg-[#16a34a]"}`}
              style={{ width: `${Math.max(3, pct)}%` }} />
          </div>
        )}
        <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1.5">
          <CalendarClock size={12} /> {t("pc.resets", { date: fmtDate(credits.quotaResetsAt) })}
        </p>
      </div>

      <div className={`mt-4 grid gap-3 ${(credits.grantedCredits || 0) > 0 ? "sm:grid-cols-2 lg:grid-cols-4" : "sm:grid-cols-3"}`}>
        <div className="rounded-xl border border-border p-3" data-testid="plan-card-rollover">
          <p className="text-lg font-extrabold text-foreground">{credits.rolloverCredits || 0}</p>
          <p className="text-xs text-muted-foreground">{t("pc.rolledOver")}</p>
          <p className="text-[11px] text-muted-foreground mt-0.5">
            {credits.rollsOver ? t("pc.rollsOverNote", { cap: cap == null ? "∞" : cap }) : t("pc.noRollover")}
          </p>
        </div>
        <div className="rounded-xl border border-border p-3" data-testid="plan-card-credits">
          <p className="text-lg font-extrabold text-foreground">{credits.dealCredits || 0}</p>
          <p className="text-xs text-muted-foreground">{t("pc.creditsHeld")}</p>
          <p className="text-[11px] text-muted-foreground mt-0.5">{t("pc.boughtNote")}</p>
        </div>
        {(credits.grantedCredits || 0) > 0 && (
          <div className="rounded-xl border border-[#16a34a]/30 bg-[#e7f6ec] dark:bg-[#16341f] p-3" data-testid="plan-card-gifted">
            <p className="text-lg font-extrabold text-foreground">{credits.grantedCredits}</p>
            <p className="text-xs text-muted-foreground">{t("pc.giftedHeld")}</p>
            <p className="text-[11px] text-muted-foreground mt-0.5">
              {(credits.creditGrants || []).filter((g) => g.expiresAt)
                .map((g) => t("pc.giftedUseBy", { n: g.credits, date: fmtDate(g.expiresAt) })).join(" · ")
                || t("pc.giftedNote")}
            </p>
          </div>
        )}
        <div className="rounded-xl border border-border p-3" data-testid="plan-card-locations">
          <p className="text-lg font-extrabold text-foreground flex items-center gap-1.5">
            <MapPin size={15} className="text-[#16a34a]" />
            {credits.branchesUsed || 0}/{credits.branchLimit == null ? "∞" : credits.branchLimit}
          </p>
          <p className="text-xs text-muted-foreground">
            {t("pc.locations")}{credits.extraBranches ? ` · ${t("pc.nBought", { n: credits.extraBranches })}` : ""}
          </p>
          <p className="text-[11px] text-muted-foreground mt-0.5">{t("pc.maxDaysShort", { n: credits.maxDealDays })}</p>
        </div>
      </div>

      {credits.dealCredits > 0 && (
        <p className="text-xs text-muted-foreground mt-3" data-testid="plan-card-credit-note">{t("pc.creditNote")}</p>
      )}
    </div>
  );
};

export default PlanCard;
